/**
 * The link to the EinkaufsZettel (docs/18-vorratsbuch.md, Anbindung an den EinkaufsZettel, V1).
 * Two directions, both as in the EinkaufsZettel's link to SpeisePlan:
 *   - Nachkaufen: what the restock rules ask for goes to a shopping list as one delivery
 *     (`kind: "stock"`), always the whole current need, one item per base product.
 *   - Einlagern: a finished shopping trip comes back as an Eingangsbeleg (inbound.ts) and is booked
 *     in the Eingang like a delivery note: lines to articles, storage, best-before, once per trip.
 */
import { baseProduct } from "./base-products.ts";
import { CONTAINERS } from "./drinks.ts";
import { quantityOf } from "./inbound.ts";
import type { Inbound, InboundLine } from "./inbound.ts";
import { stockLines } from "./ledger.ts";
import type { State } from "./ledger.ts";
import { isPantry, pantrySize, restockNeed } from "./pantry.ts";
import type { Entity } from "./entities.ts";

// ---- Nachkaufen ----

export interface RestockArticle {
  /** Master data of the article. */
  fields: Record<string, unknown>;
  /** What to buy, in containers (or in the base unit for loose goods): restockNeed. */
  need: number;
}

export interface DeliveryQuantity {
  qty: number;
  unit: string | null;
  text: string;
}

export interface DeliveryItem {
  key: string;
  name: string;
  quantities: DeliveryQuantity[];
  organic?: boolean;
  note?: string;
}

const containerWord = (f: Record<string, unknown>) => (typeof f.container === "string" && f.container in CONTAINERS ? f.container : "Packung");

/**
 * The items of the delivery „stock“: one per base product (its usual name, its id as key, as the
 * contract wants it), the quantities per article in containers („2 Packung“) or, for loose goods,
 * in the base unit („500 g“). Articles without base product stay on VorratsBuch's own list.
 */
export function restockDelivery(articles: readonly RestockArticle[]): DeliveryItem[] {
  const items = new Map<string, DeliveryItem>();
  for (const { fields: f, need } of articles) {
    const base = baseProduct(f.base);
    if (need <= 0 || !base) continue;
    const key = base.id;
    const name = base.names[0]!;
    const item = items.get(key) ?? { key, name, quantities: [] };
    const sized = pantrySize(f) !== null;
    const unit = sized ? containerWord(f) : typeof f.unit === "string" && f.unit !== "Stk." ? f.unit : "Stück";
    const known = item.quantities.find((q) => q.unit === unit);
    if (known) {
      known.qty += need;
      known.text = `${known.qty} ${unit}`;
    } else if (item.quantities.length < 5) item.quantities.push({ qty: need, unit, text: `${need} ${unit}` });
    if (f.organic === true) item.organic = true;
    items.set(key, item);
  }
  return [...items.values()].sort((a, b) => a.name.localeCompare(b.name, "de"));
}

/** „Immer automatisch senden“ (F19): the article goes to the shopping list without a tap. */
export const restockAuto = (f: Record<string, unknown>) => f.restockAuto === true;

export interface RestockDue extends RestockArticle {
  article: string;
}

/**
 * What the restock rules ask for now, over the household's whole stock, as the server sees it:
 * VorratsBuch's articles (not archived) with a need, counted with what is available, that is
 * without what a recipe holds (V3, `reserved` per article in the base unit). Sorted by article.
 */
export function restockDue(state: State, articles: Iterable<Entity>, reserved: ReadonlyMap<string, number> = new Map()): RestockDue[] {
  const stock = new Map<string, number>();
  for (const line of stockLines(state)) stock.set(line.article, (stock.get(line.article) ?? 0) + line.qty);
  const out: RestockDue[] = [];
  for (const e of articles) {
    const f = e.fields;
    if (e.type !== "article" || f.archived === true || !isPantry(f)) continue;
    const need = restockNeed((stock.get(e.id) ?? 0) - (reserved.get(e.id) ?? 0), f);
    if (need > 0) out.push({ article: e.id, fields: f, need });
  }
  return out.sort((a, b) => a.article.localeCompare(b.article));
}

/**
 * The restock that goes out by itself (F19): what is due of the articles marked „immer automatisch“
 * and of those someone confirmed with „An EinkaufsZettel senden“. A confirmed article no longer due
 * drops out of `confirmed`: when it runs low again, it waits for a tap again.
 */
export function automaticRestock(due: readonly RestockDue[], confirmed: ReadonlySet<string>): { send: RestockDue[]; confirmed: Set<string> } {
  const send = due.filter((d) => restockAuto(d.fields) || confirmed.has(d.article));
  const dueIds = new Set(due.map((d) => d.article));
  return { send, confirmed: new Set([...confirmed].filter((id) => dueIds.has(id))) };
}

// ---- Einlagern ----

export interface PurchaseItem {
  name: string;
  quantity: number | null;
  unit: string | null;
  organic?: boolean;
  category?: string;
  product?: string;
}

export interface Purchase {
  id: number;
  /** ISO time of the end of the trip. */
  at: string;
  list: { id: string; name: string };
  by?: string;
  items: PurchaseItem[];
}

/** Drinks belong to KellerLog; in VorratsBuch they are „nicht einlagern“ until KellerLog has an Eingang. */
export const isDrinkPurchase = (i: PurchaseItem) => i.category === "getraenke" || baseProduct(i.product)?.aisle === "getraenke";

/** A finished trip as Eingangsbeleg: one line per item, the key against booking it twice. */
export function inboundFromPurchase(p: Purchase): Inbound {
  const day = p.at.slice(0, 10);
  const lines: InboundLine[] = p.items.map((i, n) => {
    const unit = i.unit ?? "Stück";
    const qty = i.quantity ?? 1;
    return {
      line: n + 1,
      text: i.name,
      ...quantityOf(i.name, qty, unit),
      qty,
      unit,
      ...(i.organic ? { organic: true } : {}),
      ...(isDrinkPurchase(i) ? { group: "Getränke" } : {}),
      ...(i.product && baseProduct(i.product) ? { fields: { base: i.product } } : {}),
    };
  });
  return {
    channel: "shopping-list",
    format: "einkaufszettel",
    kind: "delivery",
    supplier: "EinkaufsZettel",
    ref: p.list.name,
    key: `einkaufszettel:${p.id}`,
    date: day,
    lines,
    notes: [],
  };
}
